import React, { useMemo } from 'react'

import { FormElementsRegistryContextProvider } from '@collaborate-health/forms'
import {
  createRegistry as createElementsRegistry,
  TEXT_CONTROL,
  TEXTAREA_CONTROL,
  CHECKBOXLIST_CONTROL,
  RADIOBUTTONLIST_CONTROL,
  SELECTLIST_CONTROL,
  DATE_CONTROL,
  PHONE_CONTROL,
  QR_CODE_CONTROL,
  EMAIL_CONTROL,
  createDefaultAction,
  createDefaultElement,
  DATETIME_CONTROL,
  createBaseElementConfigSchema,
  getBaseActionConfigSchema,
  getBaseControlConfigSchema,
} from '@collaborate-health/forms/registry/elements/defaults'

import { AccountTreeOutlined, InsightsOutlined, ViewTimelineOutlined } from '@mui/icons-material'

import { useSelector } from '@tabeeb/store'
import { getIsFormActionEnabled, getShowMenuAI } from '@tabeeb/modules/appConfigState/selectors'

import {
  ControlType,
  FormActionCreateOrUpdateRequest,
  FormConditionCreateOrUpdateRequest,
  FormControlAttributeCreateOrUpdateRequest,
  FormControlCreateOrUpdateRequest,
  FormControlDependencyCreateOrUpdateRequest,
  FormControlInfoRequest,
  FormControlOptionCreateOrUpdateRequest,
  FormControlValidationCreateOrUpdateRequest,
  FormCounterCreateOrUpdateRequest,
  FormTabsPanelCreateOrUpdateRequest,
} from '@tabeeb/contracts/main-api/swagger-main-api'
import { formTitleFormatter } from '@tabeeb/modules/shared/utils/text'

import { array, number, SchemaOf, object, string, mixed } from 'yup'
import {
  maxLengthWithName,
  noWhitespaces,
  requiredFieldWithName,
} from '@tabeeb/modules/shared/utils/validationErrorMessages'
import { getDefaultControlByControlType } from '@tabeeb/modules/createFormPage/services/mapping'
import FormAction from '../components/FormAction'
import FormCounter from '../components/FormCounter'
import FormTabPanel from '../components/FormTabPanel'

export const formControlOptionCreateOrUpdateRequestSchema: SchemaOf<FormControlOptionCreateOrUpdateRequest> =
  object().shape({
    Id: number().nullable().optional(),
    Value: string().min(1).max(128).required(),
    Order: number().required(),
    Conditions: array().of(object()).nullable().optional(), // Should use formConditionCreateOrUpdateRequestSchema
  })

export const formControlInfoRequestSchema: SchemaOf<FormControlInfoRequest> = object().shape({
  Description: string().strict().nullable().trim(noWhitespaces).max(500, maxLengthWithName('Description', 500)),
  ControlLabel: string().strict().nullable().trim(noWhitespaces).max(4000, maxLengthWithName('Control Label', 4000)),
  Type: number().required(),
  Url: string().nullable(),
  FileName: string().nullable(),
  ThumbnailUrl: string().nullable(),
  EncodedUrl: string().nullable(),
})

export const formControlAttributeCreateOrUpdateRequestSchema: SchemaOf<FormControlAttributeCreateOrUpdateRequest> =
  object().shape({
    Id: number().nullable().optional(),
    Name: string().min(1).max(150).required(),
    Value: string().min(1).max(150).required(),
  })

export const formConditionCreateOrUpdateRequestSchema: SchemaOf<FormConditionCreateOrUpdateRequest> = object().shape({
  Id: number().nullable().optional(),
  Type: number().required(), // Should be enum ConditionType
  Solution: string().max(4000).nullable().optional(),
  TriggerValue: string().max(128).nullable().optional(),
  TriggerType: number().required(), // Should be enum ConditionTriggerType
  ConditionsRecipient: array().of(object()).nullable().optional(), // Should use ConditionsRecipientRequest schema if available
})

export const formControlDependencyCreateOrUpdateRequestSchema: SchemaOf<FormControlDependencyCreateOrUpdateRequest> =
  object().shape({
    Id: number().nullable().optional(),
    ParentControlUuid: string().min(1).max(40).required(),
    Type: number().required(), // Should be enum FormControlDependencyType
    ParentControlValue: string().max(125).nullable().optional(),
  })

export const formControlValidationCreateOrUpdateRequestSchema: SchemaOf<FormControlValidationCreateOrUpdateRequest> =
  object().shape({
    Id: number().nullable().optional(),
    DependsOnControlsUuids: array().of(string()).required(),
    Type: number().required(), // Should be enum FormControlValidationType
    ControlIds: array().of(number()).nullable().optional(),
  })

export interface ContentFormsElementsRegistryContextProviderProps {
  canAddAICounters?: boolean
  canAddTabs?: boolean
  tabsDisableReason?: string
}

export const tabsRequestSchema: SchemaOf<FormTabsPanelCreateOrUpdateRequest> = object().shape({
  Id: number().optional(),
  Order: number().required(),
  Name: mixed().nullable().optional(),
  Tabs: array()
    .of(
      object().shape({
        Id: number().optional(),
        Order: number().required(),
        Value: string()
          .strict()
          .trim(noWhitespaces)
          .max(256, maxLengthWithName('Tab', 256))
          .required('Tab name is required'),
      })
    )
    .min(1, 'At least one tab is required')
    .required('Tabs are required'),
})

export const runWorkflowActionSchema: SchemaOf<FormActionCreateOrUpdateRequest> = object().shape({
  Id: number().nullable(),
  Label: string()
    .strict()
    .trim(noWhitespaces)
    .max(256, maxLengthWithName('Name', 256))
    .required(requiredFieldWithName('Name')),
  WorkflowId: string().strict().nullable().required(requiredFieldWithName('Workflow')),
  Order: number().required(),
  Tooltip: string().strict().nullable().trim(noWhitespaces).max(512, maxLengthWithName('Tooltip', 512)),
})

export const controlSchema: SchemaOf<Partial<FormControlCreateOrUpdateRequest>> = object().shape({
  // Id: number().nullable().optional(),
  // Uuid: string().max(40).nullable().optional(),
  // Label: string().min(1).max(4000).required(),
  // Order: number().min(0).max(2147483647).required(),
  // Name: string().nullable().optional(),
  // Tooltip: string().max(512).nullable().optional(),
  // Placeholder: string().max(256).nullable().optional(),
  // InitialValue: string().max(512).nullable().optional(),
  // IsRequired: boolean().required(),
  // ValidationMessage: string().nullable().optional(),
  // Options: array().of(formControlOptionCreateOrUpdateRequestSchema).nullable().optional(),
  // Type: number().required(), // Should be enum FormControlType
  // ProcessByVQAModel: boolean().required(),
  Info: formControlInfoRequestSchema.nullable().optional().default(undefined),
  // Attributes: array().of(formControlAttributeCreateOrUpdateRequestSchema).nullable().optional(),
  // Conditions: array().of(formConditionCreateOrUpdateRequestSchema).nullable().optional(),
  // Dependencies: array().of(formControlDependencyCreateOrUpdateRequestSchema).nullable().optional(),
  // Validations: array().of(formControlValidationCreateOrUpdateRequestSchema).nullable().optional(),
  // IsHidden: boolean().required(),
  // ControlType: number().optional(), // Should be enum ControlType
  // ExternalData: string().max(4000).nullable().optional(),
})

const ContentFormsElementsRegistryContextProvider: React.FC<ContentFormsElementsRegistryContextProviderProps> = ({
  children,
  canAddTabs = true,
  tabsDisableReason,
  canAddAICounters = true,
}) => {
  const isAIEnabled = useSelector(getShowMenuAI)
  const isFormActionEnabled = useSelector(getIsFormActionEnabled)

  const elementsRegistry = useMemo(() => {
    const registry = createElementsRegistry()
      .addElement<'control', FormControlCreateOrUpdateRequest>({
        ...TEXT_CONTROL,
        configSchema: getBaseControlConfigSchema({
          metaSchema: controlSchema,
        }),
        getDefaultElement: (overrides) => ({
          ...TEXT_CONTROL.getDefaultElement(overrides),
          meta: getDefaultControlByControlType(ControlType.Text),
        }),
      })
      .addElement<'control', FormControlCreateOrUpdateRequest>({
        ...TEXTAREA_CONTROL,
        configSchema: getBaseControlConfigSchema({ metaSchema: controlSchema }),
        getDefaultElement: (overrides) => ({
          ...TEXTAREA_CONTROL.getDefaultElement(overrides),
          meta: getDefaultControlByControlType(ControlType.Textarea),
        }),
      })
      .addElement<'control', FormControlCreateOrUpdateRequest>({
        ...CHECKBOXLIST_CONTROL,
        configSchema: getBaseControlConfigSchema({ metaSchema: controlSchema }),
        getDefaultElement: (overrides) => ({
          ...CHECKBOXLIST_CONTROL.getDefaultElement(overrides),
          meta: getDefaultControlByControlType(ControlType.Checkboxlist),
        }),
      })
      .addElement<'control', FormControlCreateOrUpdateRequest>({
        ...RADIOBUTTONLIST_CONTROL,
        configSchema: getBaseControlConfigSchema({ metaSchema: controlSchema }),
        getDefaultElement: (overrides) => ({
          ...RADIOBUTTONLIST_CONTROL.getDefaultElement(overrides),
          meta: getDefaultControlByControlType(ControlType.Radiobuttonlist),
        }),
      })
      .addElement<'control', FormControlCreateOrUpdateRequest>({
        ...SELECTLIST_CONTROL,
        configSchema: getBaseControlConfigSchema({ metaSchema: controlSchema }),
        getDefaultElement: (overrides) => ({
          ...SELECTLIST_CONTROL.getDefaultElement(overrides),
          meta: getDefaultControlByControlType(ControlType.Selectlist),
        }),
      })
      .addElement<'control', FormControlCreateOrUpdateRequest>({
        ...DATE_CONTROL,
        configSchema: getBaseControlConfigSchema({ metaSchema: controlSchema }),
        getDefaultElement: (overrides) => ({
          ...DATE_CONTROL.getDefaultElement(overrides),
          meta: getDefaultControlByControlType(ControlType.Date),
        }),
      })
      .addElement<'control', FormControlCreateOrUpdateRequest>({
        ...DATETIME_CONTROL,
        configSchema: getBaseControlConfigSchema({ metaSchema: controlSchema }),
        getDefaultElement: (overrides) => ({
          ...DATETIME_CONTROL.getDefaultElement(overrides),
          meta: getDefaultControlByControlType(ControlType.DateTime),
        }),
      })
      .addElement<'control', FormControlCreateOrUpdateRequest>({
        ...PHONE_CONTROL,
        configSchema: getBaseControlConfigSchema({ metaSchema: controlSchema }),
        getDefaultElement: (overrides) => ({
          ...PHONE_CONTROL.getDefaultElement(overrides),
          meta: getDefaultControlByControlType(ControlType.Text),
        }),
      })
      .addElement<'control', FormControlCreateOrUpdateRequest>({
        ...QR_CODE_CONTROL,
        configSchema: getBaseControlConfigSchema({ metaSchema: controlSchema }),
        getDefaultElement: (overrides) => ({
          ...QR_CODE_CONTROL.getDefaultElement(overrides),
          meta: getDefaultControlByControlType(ControlType.Text),
        }),
      })
      .addElement<'control', FormControlCreateOrUpdateRequest>({
        ...EMAIL_CONTROL,
        configSchema: getBaseControlConfigSchema({ metaSchema: controlSchema }),
        getDefaultElement: (overrides) => ({
          ...EMAIL_CONTROL.getDefaultElement(overrides),
          meta: getDefaultControlByControlType(ControlType.Text),
        }),
      })

    registry.addElementsGroup('tabs', 'Tabs', FormTabPanel).addElement<'tabs', FormTabsPanelCreateOrUpdateRequest>({
      icon: ViewTimelineOutlined,
      elementType: 'tabs',
      label: 'Tabs',
      type: 'default',
      disabled: !canAddTabs,
      description: canAddTabs ? undefined : tabsDisableReason,
      getDefaultElement: (props) => ({
        ...createDefaultElement(props),
        elementType: 'tabs',
        type: 'default',
        meta: {
          Order: 0,
          Name: 'Unnamed',
          Tabs: [
            {
              Value: 'Tab 1',
              Order: 0,
            },
            {
              Value: 'Tab 2',
              Order: 1,
            },
            {
              Value: 'Tab 3',
              Order: 2,
            },
          ],
        },
      }),
      configSchema: createBaseElementConfigSchema('tabs', {
        metaSchema: tabsRequestSchema,
      }),
    })

    registry
      .addElementsGroup('aiCounter', 'Counters', FormCounter)
      .addElement<'aiCounter', FormCounterCreateOrUpdateRequest>({
        icon: InsightsOutlined,
        elementType: 'aiCounter',
        label: 'AI Counter',
        hidden: true,
        type: 'default',
        disabled: !canAddAICounters,
        description: canAddAICounters
          ? undefined
          : formTitleFormatter.format('Either counter or tabs can be added to the form'),
        getDefaultElement: (props) => ({
          ...createDefaultElement(props),
          elementType: 'aiCounter',
          type: 'default',
          meta: {
            CounterLimit: null,
            AIObjectId: null!,
            CountAIObjects: false,
            Label: 'New Counter',
            Order: 0,
            IncrementValue: 0,
            IncrementPeriod: 0,
            Conditions: [],
          },
        }),
      })

    registry.addElement<'action', FormActionCreateOrUpdateRequest>({
      icon: AccountTreeOutlined,
      elementType: 'action',
      label: 'Run Workflow',
      type: 'run-workflow',
      hidden: !isFormActionEnabled,
      component: FormAction,
      getDefaultElement: (props) => ({
        ...createDefaultAction<FormActionCreateOrUpdateRequest>('run-workflow')(props),
        meta: {
          Id: undefined,
          Label: 'Run Workflow',
          WorkflowId: null!,
          Order: 0,
          Tooltip: '',
        },
      }),
      configSchema: getBaseActionConfigSchema({
        nameMaxLength: 256,
        metaSchema: runWorkflowActionSchema,
      }),
    })

    return registry
  }, [canAddAICounters, canAddTabs, isAIEnabled, isFormActionEnabled, tabsDisableReason])

  return (
    <FormElementsRegistryContextProvider registry={elementsRegistry}>{children}</FormElementsRegistryContextProvider>
  )
}

export default ContentFormsElementsRegistryContextProvider

